import {
  BankAccount,
  Invoice,
  Bill,
  Transaction,
  Scenario,
  DataSource,
  DashboardMetrics,
  ForecastDataPoint,
} from '../types';

// Import from comprehensive demo database
import DemoDatabase from './demo-database';
import { getForecastTerm } from '@/lib/terminology-config';

// BuildRight Construction Ltd Demo Data
// REALISTIC CONSTRUCTION COMPANY SCENARIO - ALL NUMBERS TIE UP
// Now powered by comprehensive database with 300+ transactions

export const DEMO_BANK_ACCOUNTS: BankAccount[] = DemoDatabase.bankAccounts;

// Total Current Cash: £247,000

export const DEMO_INVOICES: Invoice[] = DemoDatabase.invoices;

// Total Outstanding Invoices: £328,500 (Overdue £63.2k + Due £120.3k + Future £145k)

export const DEMO_BILLS: Bill[] = DemoDatabase.bills;

// Total Supplier Payments: £125,000 (Due £78.5k + Future £46.5k)

export const DEMO_TRANSACTIONS: Transaction[] = DemoDatabase.transactions;

// Legacy transactions for backward compatibility (first 15 transactions)
export const DEMO_TRANSACTIONS_LEGACY: Transaction[] = [
  // RECENT SETTLED
  {
    id: 'txn-1',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    description: 'Materials - Jewson',
    category: 'Materials',
    amount: -5200,
    type: 'expense',
    status: 'Settled',
    source: 'Purchase Ledger',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-2',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    description: 'Payment from ClientCo',
    category: 'Uncategorised',
    amount: 15000,
    type: 'income',
    status: 'Settled',
    source: 'Sales Ledger',
    transactionType: 'Receipt',
  },
  {
    id: 'txn-3',
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    description: 'Payroll - Fortnightly',
    category: 'Payroll',
    amount: -45000,
    type: 'expense',
    status: 'Settled',
    source: 'Purchase Ledger',
    transactionType: 'Payment',
  },
  {
    id: 'txn-4',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    description: 'Subcontractor - ABC Ltd',
    category: 'Subcontractors',
    amount: -12000,
    type: 'expense',
    status: 'Settled',
    source: 'Purchase Ledger',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-5',
    date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    description: 'Office Rent',
    category: 'Rent',
    amount: -3500,
    type: 'expense',
    status: 'Settled',
    source: 'Purchase Ledger',
    transactionType: 'Payment',
  },
  
  // UPCOMING OUTSTANDING - Next 30 days
  {
    id: 'txn-6',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    description: 'Payroll - Fortnightly',
    category: 'Payroll',
    amount: -45000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    transactionType: 'Payment',
  },
  {
    id: 'txn-7',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    description: 'Expected payment - BigCo Ltd (INV-1245)',
    category: 'Uncategorised',
    amount: 35000,
    type: 'income',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Sales Ledger',
    reference: 'INV-1245',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-8',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    description: 'Jewson - Materials (BILL-5678)',
    category: 'Materials',
    amount: -15000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    reference: 'BILL-5678',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-9',
    date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    description: 'Expected payment - TechStart Ltd (INV-1250)',
    category: 'Uncategorised',
    amount: 18000,
    type: 'income',
    status: 'Outstanding',
    confidence: 'probable',
    source: 'Sales Ledger',
    reference: 'INV-1250',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-10',
    date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    description: 'XYZ Equipment Hire (BILL-5685)',
    category: 'Equipment',
    amount: -8000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    reference: 'BILL-5685',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-11',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    description: 'Travis Perkins (BILL-5680)',
    category: 'Materials',
    amount: -12500,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    reference: 'BILL-5680',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-12',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    description: 'Expected payment - PropertyDev Ltd (INV-1255)',
    category: 'Uncategorised',
    amount: 50000,
    type: 'income',
    status: 'Outstanding',
    confidence: 'probable',
    source: 'Sales Ledger',
    reference: 'INV-1255',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-13',
    date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
    description: 'Payroll - Fortnightly',
    category: 'Payroll',
    amount: -45000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    transactionType: 'Payment',
  },
  {
    id: 'txn-14',
    date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
    description: 'ABC Subcontractors (BILL-5682)',
    category: 'Subcontractors',
    amount: -25000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    reference: 'BILL-5682',
    transactionType: 'Invoice',
  },
  {
    id: 'txn-15',
    date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000),
    description: 'BuildSupply Co (BILL-5688)',
    category: 'Materials',
    amount: -18000,
    type: 'expense',
    status: 'Outstanding',
    confidence: 'certain',
    source: 'Purchase Ledger',
    reference: 'BILL-5688',
    transactionType: 'Invoice',
  },
];

// Generate forecast data for 90 days - DETERMINISTIC AND REALISTIC
export const generateForecastData = (scenarioType: 'base' | 'conservative' | 'optimistic'): ForecastDataPoint[] => {
  const data: ForecastDataPoint[] = [];
  const startingCash = 247000; // Total from all bank accounts
  let balance = startingCash;
  
  // REALISTIC CONSTRUCTION COMPANY CASH FLOW
  // Base scenario: £247k → £70.3k over 90 days (£176.7k decline)
  // MUST MATCH 12-Month View (Month 3) and 3-Year View (Q1 End)
  // This represents a cash flow crisis requiring AI recommendations
  
  for (let i = 0; i <= 90; i++) {
    const date = new Date(Date.now() + i * 24 * 60 * 60 * 1000);
    const dayOfWeek = date.getDay();
    
    let dailyInflow = 0;
    let dailyOutflow = 0;
    
    // Day 0 (today) - no transactions, just show current balance
    if (i === 0) {
      dailyInflow = 0;
      dailyOutflow = 0;
    }
    // Weekend - minimal activity
    else if (dayOfWeek === 0 || dayOfWeek === 6) {
      dailyInflow = 0;
      dailyOutflow = 500; // Minimal costs
    } else {
      // Weekday - normal operations
      dailyInflow = 1500; // Small daily receipts (reduced - clients paying slower)
      dailyOutflow = 3000; // Daily operating costs (increased - cost pressures)
    }
    
    // SCHEDULED INVOICE PAYMENTS (based on DEMO_INVOICES)
    if (i === 5) dailyInflow += 35000; // BigCo Ltd (INV-1245)
    if (i === 10) dailyInflow += 18000; // TechStart Ltd (INV-1250)
    if (i === 12) dailyInflow += 7800; // SmallCo (INV-1258)
    if (i === 25) dailyInflow += 45000; // MegaCorp (INV-1260)
    if (i === 30) dailyInflow += 12000; // ClientCo (INV-1262)
    if (i === 40) dailyInflow += 28000; // BuildCorp (INV-1252)
    if (i === 50) dailyInflow += 22000; // TechStart Ltd (INV-1254)
    if (i === 60) dailyInflow += 38000; // BigCo Ltd (INV-1256)
    // RetailCo £9.5k - DELAYED beyond 90 days
    // PropertyDev £50k - DELAYED beyond 90 days
    
    // OVERDUE INVOICES - gradually collected
    if (i === 18) dailyInflow += 15000; // MedCo Inc (INV-1205)
    if (i === 25) dailyInflow += 5200; // ClientCo (INV-1189)
    if (i === 35) dailyInflow += 8500; // SmallCo (INV-1198)
    if (i === 45) dailyInflow += 22000; // BigCo Ltd (INV-1234)
    if (i === 55) dailyInflow += 12500; // BuildCorp (INV-1210)
    
    // PROJECT MILESTONES - ongoing work
    if (i === 20) dailyInflow += 25000; // Riverside project milestone
    if (i === 50) dailyInflow += 30000; // Westfield project milestone
    if (i === 75) dailyInflow += 25000; // New project deposit
    if (i === 85) dailyInflow += 15000; // Additional milestone payment
    
    // PAYROLL - Every 14 days (biweekly) - CANNOT BE DELAYED
    if (i === 7) dailyOutflow += 45000;
    if (i === 21) dailyOutflow += 45000;
    if (i === 35) dailyOutflow += 45000;
    if (i === 49) dailyOutflow += 45000;
    if (i === 63) dailyOutflow += 45000;
    if (i === 77) dailyOutflow += 45000;
    
    // SUPPLIER PAYMENTS (based on DEMO_BILLS)
    if (i === 7) dailyOutflow += 15000; // Jewson (BILL-5678)
    if (i === 10) dailyOutflow += 8000; // XYZ Equipment Hire (BILL-5685)
    if (i === 14) dailyOutflow += 12500; // Travis Perkins (BILL-5680)
    if (i === 21) dailyOutflow += 25000; // ABC Subcontractors (BILL-5682)
    if (i === 28) dailyOutflow += 18000; // BuildSupply Co (BILL-5688)
    if (i === 45) dailyOutflow += 14000; // Jewson (BILL-5690)
    if (i === 60) dailyOutflow += 22000; // ABC Subcontractors (BILL-5692)
    if (i === 75) dailyOutflow += 10500; // XYZ Equipment Hire (BILL-5694)
    
    // MONTHLY FIXED COSTS
    if (i === 1 || i === 31 || i === 61) {
      dailyOutflow += 3500; // Rent
      dailyOutflow += 2800; // Insurance
      dailyOutflow += 1500; // Utilities
    }
    
    // VAT PAYMENT (quarterly)
    if (i === 28) dailyOutflow += 15000; // VAT payment
    
    // SCENARIO ADJUSTMENTS
    if (scenarioType === 'conservative') {
      // Even worse - major client delays
      if (i >= 30) {
        dailyInflow *= 0.50; // 50% reduction
        // More delays
        if (i === 25) dailyInflow -= 45000; // MegaCorp delays payment
        if (i === 80) dailyInflow += 45000; // Paid very late
      }
    } else if (scenarioType === 'optimistic') {
      // New client secured - increased income from day 20
      if (i >= 20) {
        dailyInflow *= 1.50; // 50% increase
        // Additional project payments
        if (i === 25) dailyInflow += 30000; // New project deposit
        if (i === 55) dailyInflow += 40000; // New project milestone
        if (i === 85) dailyInflow += 35000; // New project completion
      }
    }
    
    balance = balance + dailyInflow - dailyOutflow;
    
    data.push({
      date,
      balance,
      incoming: dailyInflow,
      outgoing: dailyOutflow,
      scenario: scenarioType,
    });
  }
  
  return data;
};

// Derived alignment constant — Day 90 balance from the base forecast.
// Used by 12-month (Month 3) and 3-year (Q1 End) views to stay in sync.
// Never hardcode 60600 — change the transaction data above and this updates everywhere.
export const FORECAST_90_DAY_ENDPOINT: number = (() => {
  const data = generateForecastData('base');
  return Math.round(data[90].balance);
})();

export const DEMO_SCENARIOS: Scenario[] = [
  {
    id: 'scenario-1',
    name: `Base ${getForecastTerm(true)}`,
    description: 'Current reality based on existing commitments and historical patterns',
    type: 'base',
    cashPosition: 247000,
    minCashDate: new Date(Date.now() + 63 * 24 * 60 * 60 * 1000),
    minCashAmount: 165000,
    runway: 6,
    adjustments: [],
  },
  {
    id: 'scenario-2',
    name: 'Conservative',
    description: 'BigCo project delays - reduced income from day 30 onwards',
    type: 'conservative',
    cashPosition: 247000,
    minCashDate: new Date(Date.now() + 70 * 24 * 60 * 60 * 1000),
    minCashAmount: 95000,
    runway: 4,
    adjustments: [
      {
        id: 'adj-1',
        description: 'BigCo project payment delayed - 35% reduction in income',
        impact: -150000,
        date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      },
    ],
  },
  {
    id: 'scenario-3',
    name: 'Optimistic',
    description: 'New client secured - increased income from day 20 onwards',
    type: 'optimistic',
    cashPosition: 247000,
    minCashDate: new Date(Date.now() + 63 * 24 * 60 * 60 * 1000),
    minCashAmount: 215000,
    runway: 8,
    adjustments: [
      {
        id: 'adj-2',
        description: 'New client contract secured - 35% increase in income plus £105k in new project payments',
        impact: 180000,
        date: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000),
      },
    ],
  },
];

export const DEMO_JURISDICTIONS = [
  {
    id: 'jur-uk-001',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    primaryCurrency: 'GBP',
    reportingCurrencies: ['GBP'],
    consolidationCurrency: 'GBP',
    isActive: true,
    statutoryPayments: [
      {
        id: 'sp-vat-001',
        jurisdictionId: 'jur-uk-001',
        name: 'VAT Return',
        frequency: 'Quarterly' as const,
        dayOfMonth: 31,
        nextDue: new Date('2025-12-31'),
        isActive: true,
      },
      {
        id: 'sp-paye-001',
        jurisdictionId: 'jur-uk-001',
        name: 'PAYE & NIC',
        frequency: 'Monthly' as const,
        dayOfMonth: 22,
        nextDue: new Date('2025-12-22'),
        isActive: true,
      },
      {
        id: 'sp-ct-001',
        jurisdictionId: 'jur-uk-001',
        name: 'Corporation Tax',
        frequency: 'Annual' as const,
        dayOfMonth: 31,
        nextDue: new Date('2026-03-31'),
        isActive: true,
      },
    ],
    createdAt: new Date('2025-01-01'),
    updatedAt: new Date('2025-01-01'),
  },
];

export const DEMO_DATA_SOURCES: DataSource[] = [
  {
    id: 'source-1',
    name: 'Access Financials (Accounting System)',
    status: 'success',
    lastSync: new Date(Date.now() - 2 * 60 * 1000),
    nextSync: new Date(Date.now() + 58 * 60 * 1000),
    recordCount: 47,
  },
  {
    id: 'source-2',
    name: 'Paycircle (Payroll System)',
    status: 'success',
    lastSync: new Date(Date.now() - 8 * 60 * 60 * 1000),
    nextSync: new Date(Date.now() + 16 * 60 * 60 * 1000),
    recordCount: 150,
  },
  {
    id: 'source-3',
    name: 'Access CRM (CRM System)',
    status: 'warning',
    lastSync: new Date(Date.now() - 3 * 60 * 60 * 1000),
    recordCount: 23,
  },
  {
    id: 'source-4',
    name: 'Financial Operations (Project Management System)',
    status: 'success',
    lastSync: new Date(Date.now() - 1 * 60 * 60 * 1000),
    nextSync: new Date(Date.now() + 23 * 60 * 60 * 1000),
    recordCount: 89,
  },
  {
    id: 'source-5',
    name: 'Budget Analysis (Excel Spreadsheet)',
    status: 'success',
    lastSync: new Date(Date.now() - 30 * 60 * 1000),
    nextSync: new Date(Date.now() + 3.5 * 60 * 60 * 1000),
    recordCount: 12,
  },
];

// CFO Strategic KPIs
export interface CFOMetrics {
  groupCash: number;
  groupRunway: number;
  liquidityRatio: number;
  daysCashOnHand: number;
  cashConversionCycle: number;
  workingCapital: number;
  operatingCashFlow: number;
  monthlyRevenue: number;
  debtorDays: number;
  creditorDays: number;
  lastQuarterCash: number;
  industryAvgDaysCash: number;
  lastQuarterCashConversion: number;
}

// DEMO_CFO_METRICS — BuildRight Construction Ltd (single entity)
// All figures derived from or consistent with financial-systems.ts source data:
//   - Cash: sum of BANK_ACCOUNTS (179,432 + 44,871 + 22,734 = £247,038)
//   - Monthly revenue: MONTHLY_REVENUE = ANNUAL_TURNOVER (£5M) ÷ 12 = £416,667
//   - Monthly operating expenses: sum of MONTHLY_EXPENSES constants = ~£63,352
//   - Days cash on hand: £247,038 ÷ (£63,352 / 30) = ~117 days
//   - Cash runway: £247,038 ÷ £63,352/month = ~3.9 months
//   - Operating cash flow: ~15% net margin on revenue = ~£62,500/month
//   - Working capital: cash + receivables (~£185K outstanding) - payables (~£95K) = ~£337K
//   - Liquidity ratio: current assets (£432K) ÷ current liabilities (~£240K) = ~1.8x
//   - Debtor days: weighted avg of customer payment terms (30–60 days) = ~45 days
//   - Creditor days: supplier payment behaviour = ~35 days
//   - Cash conversion cycle: DSO(45) + DIO(10) - DPO(35) = 20 days (low inventory, construction)
//   - Industry avg days cash: SME construction benchmark = 45 days
//   - Last quarter comparison: Q3 cash was £218K (BuildRight growing), CCC was 26 days
export const DEMO_CFO_METRICS: CFOMetrics = {
  groupCash: 247038,           // Sum of all 3 bank accounts (matches dashboard cash position)
  groupRunway: 3.9,            // Months: £247,038 ÷ £63,352 monthly operating expenses
  liquidityRatio: 1.8,         // Current assets (£432K) ÷ current liabilities (£240K)
  daysCashOnHand: 117,         // £247,038 ÷ (£63,352 / 30 days)
  cashConversionCycle: 20,     // DSO(45) + DIO(10) - DPO(35) — construction has minimal inventory
  workingCapital: 337000,      // Cash(£247K) + receivables(£185K) - payables(£95K)
  operatingCashFlow: 62500,    // ~15% net operating margin on £416,667 monthly revenue
  monthlyRevenue: 416667,      // ANNUAL_TURNOVER (£5M) ÷ 12
  debtorDays: 45,              // Weighted avg of customer payment terms (30–60 days)
  creditorDays: 35,            // Supplier payment behaviour — paying promptly
  lastQuarterCash: 218000,     // Q3 cash position — BuildRight growing quarter-on-quarter
  industryAvgDaysCash: 45,     // SME construction industry benchmark (FSB/BCIS data)
  lastQuarterCashConversion: 26, // Q3 CCC — improving as debtor collection tightens
};

// Statutory Compliance Status
export interface ComplianceItem {
  id: string;
  name: string;
  status: 'completed' | 'pending' | 'overdue';
  dueDate: Date;
  amount?: number;
  entity?: string;
}

export const DEMO_COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    id: 'comp-vat-q3',
    name: 'VAT Q3 2025',
    status: 'completed',
    dueDate: new Date('2026-01-07'),
    amount: 42000,
  },
  {
    id: 'comp-paye-nov',
    name: 'PAYE November',
    status: 'completed',
    dueDate: new Date('2025-12-22'),
    amount: 38000,
  },
  {
    id: 'comp-corp-tax',
    name: 'Corporation Tax FY24/25',
    status: 'pending',
    dueDate: new Date('2026-01-01'),
    amount: 45000,
  },
  {
    id: 'comp-paye-dec',
    name: 'PAYE December',
    status: 'pending',
    dueDate: new Date('2025-12-22'),
    amount: 39000,
  },
  {
    id: 'comp-vat-q4',
    name: 'VAT Q4 2025',
    status: 'pending',
    dueDate: new Date('2026-01-07'),
    amount: 44000,
  },
];

// DEPRECATED: This hardcoded data has been replaced by the metrics service
// Dashboard components now use getDashboardMetrics() from @/services/metricsService
// which respects data source priority configuration
//
// export const DEMO_DASHBOARD_METRICS: DashboardMetrics = {
//   currentCash: 247000,
//   projectedCash30Days: 182000,
//   projectedCash90Days: 60600,
//   runway: 4,
//   overdueInvoices: 5,
//   overdueAmount: 63200,
//   upcomingPayments: 8,
//   upcomingAmount: 125000,
//   variance: -28.5,
//   varianceAmount: -70000,
//   alerts: [...],
// };

// Board Meeting Data (External Board Member View)
export interface BoardMeeting {
  id: string;
  date: Date;
  title: string;
  status: 'completed' | 'upcoming';
  boardPackStatus?: 'approved' | 'pending';
  papersDueDate?: Date;
}

export interface BoardDecision {
  id: string;
  meetingId: string;
  description: string;
  status: 'approved' | 'pending' | 'rejected';
}

export interface BoardUpdate {
  id: string;
  date: Date;
  description: string;
  type: 'accounts' | 'audit' | 'strategic' | 'compliance';
}

export const DEMO_BOARD_MEETINGS: BoardMeeting[] = [
  {
    id: 'meeting-q3-2025',
    date: new Date('2025-11-15'),
    title: 'Q3 FY2025/26 Review',
    status: 'completed',
    boardPackStatus: 'approved',
  },
  {
    id: 'meeting-q4-2026',
    date: (() => {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 14);
      return nextMonth;
    })(),
    title: 'Q4 FY2025/26 Results & FY2026/27 Planning',
    status: 'upcoming',
    papersDueDate: (() => {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 7);
      return nextMonth;
    })(),
  },
];

export const DEMO_BOARD_DECISIONS: BoardDecision[] = [
  {
    id: 'decision-1',
    meetingId: 'meeting-q3-2025',
    description: 'Approved £500K plant & equipment capex for Q1 FY2026/27',
    status: 'approved',
  },
  {
    id: 'decision-2',
    meetingId: 'meeting-q3-2025',
    description: 'Approved additional depot lease — operational expansion',
    status: 'approved',
  },
  {
    id: 'decision-3',
    meetingId: 'meeting-q3-2025',
    description: 'Strategic review of commercial pipeline for FY2026/27',
    status: 'pending',
  },
];

// Board updates — dynamically offset from today so dates never look stale
const _boardUpdateDate1 = new Date(); _boardUpdateDate1.setDate(_boardUpdateDate1.getDate() - 14);
const _boardUpdateDate2 = new Date(); _boardUpdateDate2.setDate(_boardUpdateDate2.getDate() - 17);
const _boardUpdateDate3 = new Date(); _boardUpdateDate3.setDate(_boardUpdateDate3.getDate() - 23);

export const DEMO_BOARD_UPDATES: BoardUpdate[] = [
  {
    id: 'update-1',
    date: _boardUpdateDate1,
    description: 'Q3 management accounts circulated to board',
    type: 'accounts',
  },
  {
    id: 'update-2',
    date: _boardUpdateDate2,
    description: 'Auditor interim visit completed — no material findings',
    type: 'audit',
  },
  {
    id: 'update-3',
    date: _boardUpdateDate3,
    description: 'FY2026/27 strategic plan update shared with board',
    type: 'strategic',
  },
];

// Critical Alerts (External Board Member View)
export interface CriticalAlert {
  id: string;
  date: Date;
  severity: 'critical' | 'warning' | 'resolved';
  message: string;
  entity?: string;
  resolution?: string;
}

// Keep resolved alerts within the last 30 days so the card's "Last 30 Days" section
// always has content — offset from today so this never drifts out of the window
const _alertDate1 = new Date();
_alertDate1.setDate(_alertDate1.getDate() - 12);

export const DEMO_CRITICAL_ALERTS: CriticalAlert[] = [
  {
    id: 'alert-historical-1',
    date: _alertDate1,
    severity: 'resolved',
    message: 'Cash position approached minimum monitoring threshold (£52K)',
    resolution: 'Invoice collections accelerated — position restored to £195K',
  },
];

// Alert Thresholds for Board Monitoring
export interface AlertThreshold {
  id: string;
  name: string;
  threshold: string;
  status: 'clear' | 'warning' | 'critical';
}

export const DEMO_ALERT_THRESHOLDS: AlertThreshold[] = [
  {
    id: 'threshold-cash',
    name: 'Cash below £50K minimum threshold',
    threshold: '£50,000',
    status: 'clear',
  },
  {
    id: 'threshold-covenant',
    name: 'Covenant breaches',
    threshold: 'Any breach',
    status: 'clear',
  },
  {
    id: 'threshold-statutory',
    name: 'Overdue scheduled payments',
    threshold: 'Any overdue',
    status: 'clear',
  },
  {
    id: 'threshold-variance',
    name: 'Significant variances',
    threshold: '>15%',
    status: 'clear',
  },
];
